import React, { useState } from 'react';
import {
    Box,
    Flex,
    Link,
    Popover,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    Portal,
    useBreakpointValue,
} from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import { useCategories } from '../../../../../bff-api/categories/useCategories';
import { Text } from '../../../../../ui-components';
import {
    isBrandsCategory,
    isSaleCategory,
    toCategoriesDictionary,
} from '../../../../../utils/category-utils';
import { sanitizeHtml } from '../../../../content-asset/sanitize-html';
import { ArrowRightIcon } from '../../../../icons';
import styles from './MenuCategoryDesktop.module.scss';
import { MenuCategoryBrandsDesktop } from './menu-category-brands-desktop';
import type { Category } from '../../../../../bff-api/categories/model/category';

const BannerLayout = ({ id, banner }: { id: string; banner: string }): React.ReactElement => (
    <Flex
        data-testid={id}
        direction="column"
        rowGap={4}
        maxWidth="300px"
        textStyle="body-base"
        className={styles.menu_banner}
        dangerouslySetInnerHTML={{
            __html: sanitizeHtml(banner),
        }}
    />
);

export const MenuCategoryDesktop: React.FC = () => {
    const { data } = useCategories();
    const categories = data ?? [];
    const [activeCatLevel1, setActiveCatLevel1] = useState<Category | null>(null);
    const [activeCatLevel2, setActiveCatLevel2] = useState<Category | null>(null);

    const activeCatsLevel3 = toCategoriesDictionary(activeCatLevel2);
    const brands = activeCatLevel2?.subCategories.find(isBrandsCategory);
    const isTablet = useBreakpointValue({ base: true, lg: false });

    return (
        <>
            {categories.map((catLevel1) => {
                const isBrands = isBrandsCategory(catLevel1);
                const isSale = isSaleCategory(catLevel1);
                let categoryForLink;
                if (activeCatLevel1?.showShopAllLink) {
                    categoryForLink = activeCatLevel1;
                } else if (activeCatLevel2?.showShopAllLink) {
                    categoryForLink = activeCatLevel2;
                }

                const activeCatLevel1HasBanner = !!(
                    activeCatLevel1?.headerMenuBanner1 ||
                    activeCatLevel1?.headerMenuBanner2 ||
                    activeCatLevel1?.headerMenuBanner3 ||
                    activeCatLevel1?.headerMenuBanner4
                );

                const isOnlyLink = catLevel1.subCategories.length === 0 && !isBrands;

                return isOnlyLink ? (
                    <Link
                        key={catLevel1.ID}
                        href={catLevel1.url}
                        data-testid={`header.menu.desktop.category.${catLevel1.ID}`}
                        aria-label={`${catLevel1.displayName} Link`}
                    >
                        <Text
                            id={`header.menu.desktop.category.${catLevel1.ID}.name`}
                            color={isSale ? 'slt-red-error' : undefined}
                            style="bold"
                        >
                            {catLevel1.displayName}
                        </Text>
                    </Link>
                ) : (
                    <Popover
                        key={catLevel1.ID}
                        trigger={isTablet ? 'click' : 'hover'}
                        variant="slt-outline"
                        onOpen={(): void => {
                            setActiveCatLevel1(catLevel1);
                            setActiveCatLevel2(catLevel1.subCategories[0]);
                        }}
                    >
                        <PopoverTrigger>
                            {/* TODO: for PWA PLP release with AB test we need to handle the link to go to SG or PWA */}
                            {isTablet ? (
                                <div
                                    data-testid={`header.menu.desktop.category.${catLevel1.ID}`}
                                    className="cursor-pointer"
                                >
                                    <Text
                                        id={`header.menu.desktop.category.${catLevel1.ID}.name`}
                                        color={isSale ? 'slt-red-error' : undefined}
                                        style="bold"
                                    >
                                        {catLevel1.displayName}
                                    </Text>
                                </div>
                            ) : (
                                <Link
                                    href={catLevel1.url}
                                    data-testid={`header.menu.desktop.category.${catLevel1.ID}`}
                                    aria-label={`${catLevel1.displayName} Link`}
                                >
                                    <Text
                                        id={`header.menu.desktop.category.${catLevel1.ID}.name`}
                                        color={isSale ? 'slt-red-error' : undefined}
                                        style="bold"
                                    >
                                        {catLevel1.displayName}
                                    </Text>
                                </Link>
                            )}
                        </PopoverTrigger>

                        {!isBrands &&
                        activeCatLevel1?.ID === catLevel1.ID &&
                        activeCatLevel1.subCategories.length > 0 ? (
                            <Portal>
                                <PopoverContent padding={8}>
                                    <PopoverBody
                                        data-testid={`header.menu.desktop.category.${catLevel1.ID}.content`}
                                        columnGap={8}
                                        flexDirection="row"
                                    >
                                        {catLevel1.subCategories.length > 0 && (
                                            <Flex
                                                flexDirection="column"
                                                flexShrink={0}
                                                rowGap={4}
                                                width="15%"
                                            >
                                                {catLevel1.subCategories.map((catLevel2) => (
                                                    <Link
                                                        href={catLevel2.url}
                                                        key={catLevel2.ID}
                                                        data-testid={`header.menu.desktop.category.lvl2.${catLevel2.ID}`}
                                                        onMouseEnter={(): void =>
                                                            setActiveCatLevel2(catLevel2)
                                                        }
                                                    >
                                                        <Text
                                                            id={`header.menu.desktop.category.lvl2.${catLevel2.ID}.name`}
                                                            size="small"
                                                            style="bolder"
                                                        >
                                                            {catLevel2.displayName}
                                                        </Text>
                                                    </Link>
                                                ))}
                                            </Flex>
                                        )}

                                        <Flex direction="column" grow={1} rowGap={12}>
                                            {(Object.keys(activeCatsLevel3).length > 0 ||
                                                activeCatLevel2?.headerMenuBanner1) && (
                                                <Flex columnGap={8} justify="space-between">
                                                    {Object.keys(activeCatsLevel3).map((key) => (
                                                        <Flex
                                                            key={key}
                                                            direction="column"
                                                            rowGap={12}
                                                            // width="192px"
                                                            flexShrink={0}
                                                        >
                                                            {activeCatsLevel3[key].map(
                                                                (catLevel3) => (
                                                                    <Flex
                                                                        direction="column"
                                                                        key={catLevel3.ID}
                                                                    >
                                                                        <Link
                                                                            href={catLevel3.url}
                                                                            data-testid={`header.menu.desktop.category.lvl3.${catLevel3.ID}`}
                                                                        >
                                                                            <Flex
                                                                                columnGap={2}
                                                                                alignItems="center"
                                                                                _hover={{
                                                                                    columnGap: 4,
                                                                                }}
                                                                            >
                                                                                <Text
                                                                                    size="small"
                                                                                    style="bolder"
                                                                                >
                                                                                    {
                                                                                        catLevel3.displayName
                                                                                    }
                                                                                </Text>
                                                                                <ArrowRightIcon />
                                                                            </Flex>
                                                                        </Link>

                                                                        <Flex
                                                                            direction="column"
                                                                            rowGap={4}
                                                                            marginTop={4}
                                                                        >
                                                                            {catLevel3.subCategories.map(
                                                                                (catLevel4) => (
                                                                                    <Link
                                                                                        key={
                                                                                            catLevel4.ID
                                                                                        }
                                                                                        href={
                                                                                            catLevel4.url
                                                                                        }
                                                                                        data-testid={`header.menu.desktop.category.lvl4.${catLevel4.ID}`}
                                                                                    >
                                                                                        <Text size="small">
                                                                                            {
                                                                                                catLevel4.displayName
                                                                                            }
                                                                                        </Text>
                                                                                    </Link>
                                                                                ),
                                                                            )}
                                                                        </Flex>
                                                                    </Flex>
                                                                ),
                                                            )}
                                                        </Flex>
                                                    ))}

                                                    {activeCatLevel2?.headerMenuBanner1 ? (
                                                        <Flex
                                                            data-testid={`header.menu.desktop.category.lvl2.${activeCatLevel2.ID}.banner`}
                                                            direction="column"
                                                            rowGap={4}
                                                            width="248px"
                                                            textStyle="body-base"
                                                            className={styles.menu_banner}
                                                            dangerouslySetInnerHTML={{
                                                                __html: sanitizeHtml(
                                                                    activeCatLevel2.headerMenuBanner1,
                                                                ),
                                                            }}
                                                        />
                                                    ) : null}
                                                </Flex>
                                            )}

                                            {activeCatLevel1HasBanner && (
                                                <Flex columnGap={8}>
                                                    {activeCatLevel1?.headerMenuBanner1 ? (
                                                        <BannerLayout
                                                            id={`header.menu.desktop.category.lvl1.${activeCatLevel1.ID}.banner1`}
                                                            banner={
                                                                activeCatLevel1.headerMenuBanner1
                                                            }
                                                        />
                                                    ) : null}

                                                    {activeCatLevel1?.headerMenuBanner2 ? (
                                                        <BannerLayout
                                                            id={`header.menu.desktop.category.lvl1.${activeCatLevel1.ID}.banner2`}
                                                            banner={
                                                                activeCatLevel1.headerMenuBanner2
                                                            }
                                                        />
                                                    ) : null}

                                                    {activeCatLevel1?.headerMenuBanner3 ? (
                                                        <BannerLayout
                                                            id={`header.menu.desktop.category.lvl1.${activeCatLevel1.ID}.banner3`}
                                                            banner={
                                                                activeCatLevel1.headerMenuBanner3
                                                            }
                                                        />
                                                    ) : null}

                                                    {activeCatLevel1?.headerMenuBanner4 ? (
                                                        <BannerLayout
                                                            id={`header.menu.desktop.category.lvl1.${activeCatLevel1.ID}.banner4`}
                                                            banner={
                                                                activeCatLevel1.headerMenuBanner4
                                                            }
                                                        />
                                                    ) : null}
                                                </Flex>
                                            )}

                                            {brands || categoryForLink?.showShopAllLink ? (
                                                <Flex
                                                    justifyContent="flex-end"
                                                    alignItems="flex-end"
                                                    height="100%"
                                                >
                                                    {brands ? (
                                                        <Flex columnGap={2} width="100%">
                                                            <Text
                                                                id="header.menu.desktop.category.lvl3.brands"
                                                                size="small"
                                                                style="bolder"
                                                            >
                                                                {brands.displayName}:
                                                            </Text>

                                                            <Flex
                                                                wrap="wrap"
                                                                maxWidth="650px"
                                                                gap={2}
                                                            >
                                                                {brands.subCategories.map(
                                                                    ({ ID, displayName, url }) => (
                                                                        <Box
                                                                            key={ID}
                                                                            paddingX={3}
                                                                            paddingY={2}
                                                                            borderRadius={2}
                                                                            background="slt-black.50"
                                                                            _hover={{
                                                                                fontWeight: 600,
                                                                                background:
                                                                                    '#d8d8d8',
                                                                            }}
                                                                        >
                                                                            <Link
                                                                                href={url}
                                                                                data-testid={`header.menu.desktop.category.lvl4.${ID}`}
                                                                            >
                                                                                <Text
                                                                                    size="small"
                                                                                    style="bold"
                                                                                >
                                                                                    {displayName}
                                                                                </Text>
                                                                            </Link>
                                                                        </Box>
                                                                    ),
                                                                )}
                                                            </Flex>
                                                        </Flex>
                                                    ) : null}

                                                    {categoryForLink?.showShopAllLink ? (
                                                        <Link
                                                            href={categoryForLink.url}
                                                            data-testid={`header.menu.desktop.category.${categoryForLink.ID}.link`}
                                                            className="min-w-fit"
                                                        >
                                                            <Flex
                                                                grow={1}
                                                                shrink={0}
                                                                gap={2}
                                                                alignItems="center"
                                                                justifyContent="flex-end"
                                                            >
                                                                <Text size="small" style="bolder">
                                                                    <FormattedMessage
                                                                        id="header.shop.all"
                                                                        defaultMessage="Shop All {category}"
                                                                        values={{
                                                                            category:
                                                                                categoryForLink.displayName,
                                                                        }}
                                                                    />
                                                                </Text>
                                                                <ArrowRightIcon
                                                                    marginBottom={0.5}
                                                                />
                                                            </Flex>
                                                        </Link>
                                                    ) : null}
                                                </Flex>
                                            ) : null}
                                        </Flex>
                                    </PopoverBody>
                                </PopoverContent>
                            </Portal>
                        ) : null}

                        {isBrands ? (
                            <Portal>
                                <PopoverContent padding={8}>
                                    <PopoverBody flexDirection="row">
                                        <MenuCategoryBrandsDesktop brands={catLevel1} />
                                    </PopoverBody>
                                </PopoverContent>
                            </Portal>
                        ) : null}
                    </Popover>
                );
            })}
        </>
    );
};
